import React, { useState, useEffect } from 'react';
import companyService from '../../services/companyService';
import authService from '../../services/authService';
import './CompanyProfile.css';

function CompanyProfile() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    companyName: '',
    logoUrl: '',
    industry: '',
    companySize: '',
    website: '',
    address: ''
  });

  const isAdmin = authService.isAdmin();

  useEffect(() => {
    loadCompanyProfile();
  }, []);

  const loadCompanyProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await companyService.getProfile();

      // API returns data directly, not wrapped in {company: {...}}
      setCompany(data);
      setFormData({
        companyName: data.companyName || '',
        logoUrl: data.logoUrl || '',
        industry: data.industry || '',
        companySize: data.companySize || '',
        website: data.website || '',
        address: data.address || ''
      });
    } catch (err) {
      console.error('Failed to load company profile:', err);
      setError(err.message || 'Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await companyService.updateProfile(formData);
      alert('✅ Company profile updated successfully!');
      setIsEditing(false);
      loadCompanyProfile();
    } catch (err) {
      console.error('Failed to update profile:', err);
      alert('❌ Failed to update profile: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="company-profile">
        <h2>Company Profile</h2>
        <div className="loading">Loading company profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-profile">
        <h2>Company Profile</h2>
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="company-profile">
        <h2>Company Profile</h2>
        <div className="error">No company data available</div>
      </div>
    );
  }

  return (
    <div className="company-profile">
      <h2>Company Profile</h2>
      {!isEditing ? (
        <div className="profile-view">
          <p><strong>Company Name:</strong> {company.companyName}</p>
          <p><strong>Domain:</strong> @{company.domain}</p>
          <p><strong>Company ID:</strong> #{company.id}</p>
          {company.website && (
            <p><strong>Website:</strong> {company.website}</p>
          )}
          {company.industry && (
            <p><strong>Industry:</strong> {company.industry}</p>
          )}
          {company.description && (
            <p><strong>Description:</strong> {company.description}</p>
          )}
          {company.createdAt && (
            <p><strong>Member Since:</strong> {new Date(company.createdAt).toLocaleDateString()}</p>
          )}
          {isAdmin && (
            <button onClick={() => setIsEditing(true)} className="btn-edit">
              ✏️ Edit Profile
            </button>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Company Name *</label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => setFormData({...formData, companyName: e.target.value})}
              required
            />
          </div>

          <div className="form-group">
            <label>Industry</label>
            <input
              type="text"
              value={formData.industry}
              onChange={(e) => setFormData({...formData, industry: e.target.value})}
              placeholder="e.g., Technology, Finance, Healthcare"
            />
          </div>

          <div className="form-group">
            <label>Company Size</label>
            <select
              value={formData.companySize}
              onChange={(e) => setFormData({...formData, companySize: e.target.value})}
            >
              <option value="">Select size</option>
              <option value="1-10">1-10 employees</option>
              <option value="11-50">11-50 employees</option>
              <option value="51-200">51-200 employees</option>
              <option value="201-500">201-500 employees</option>
              <option value="500+">500+ employees</option>
            </select>
          </div>

          <div className="form-group">
            <label>Website</label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({...formData, website: e.target.value})}
              placeholder="https://example.com"
            />
          </div>

          <div className="form-group">
            <label>Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="Company address"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Logo URL (optional)</label>
            <input
              type="url"
              value={formData.logoUrl}
              onChange={(e) => setFormData({...formData, logoUrl: e.target.value})}
              placeholder="https://example.com/logo.png"
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={() => setIsEditing(false)} disabled={saving}>
              Cancel
            </button>
            <button type="submit" disabled={saving}>
              {saving ? 'Saving...' : '💾 Save Changes'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default CompanyProfile;